import { useReadContract, useReadContracts, useAccount } from "wagmi";
import { VAULT_ABI, ERC20_ABI, VAULT_ADDRESS, TOKEN_ADDRESS } from "../config/contracts";

const hasVault = !!VAULT_ADDRESS;

// 主奖池 + 配置批量查询
export function useVaultGlobals() {
  const { data } = useReadContracts({
    allowFailure: true,
    query: { enabled: hasVault, refetchInterval: 10_000 },
    contracts: hasVault
      ? [
          { address: VAULT_ADDRESS, abi: VAULT_ABI, functionName: "getVaultStats" },
          { address: VAULT_ADDRESS, abi: VAULT_ABI, functionName: "getPrizeConfig" },
          { address: VAULT_ADDRESS, abi: VAULT_ABI, functionName: "getLastWin" },
          { address: VAULT_ADDRESS, abi: VAULT_ABI, functionName: "lotteryEnabled" },
          { address: VAULT_ADDRESS, abi: VAULT_ABI, functionName: "holdingThreshold" },
          { address: VAULT_ADDRESS, abi: VAULT_ABI, functionName: "cooldown" },
          { address: VAULT_ADDRESS, abi: VAULT_ABI, functionName: "minHoldTime" },
          { address: VAULT_ADDRESS, abi: VAULT_ABI, functionName: "taxToken" },
        ]
      : [],
  });

  if (!hasVault || !data) {
    // 未配置金库：活动未开始 → 全部归零，不显示演示数据
    return {
      ready: false,
      stats: { poolBalance: 0n, totalTaxReceived: 0n, totalPrizesPaid: 0n,
        totalVrfFunded: 0n, totalPortalFee: 0n, totalDraws: 0n,
        tier1Wins: 0n, tier2Wins: 0n, tier3Wins: 0n, noPrizeCount: 0n },
      prizeConfig: { prob1Bps: 0, prob2Bps: 0, prob3Bps: 0, noPrizeBps: 0,
        prize1: 100_000_000_000_000_000n, prize2: 50_000_000_000_000_000n, prize3: 20_000_000_000_000_000n },
      lastWin: null,
      lotteryEnabled: false,
      holdingThreshold: 200_000n * 10n ** 18n,
      cooldown: 3_600n,
      minHoldTime: 900n,
      taxToken: TOKEN_ADDRESS || "0x0000000000000000000000000000000000000000",
    };
  }

  const r = (i) => data[i]?.result;
  const stats = r(0);
  const prize = r(1);
  const lastWin = r(2);

  return {
    ready: true,
    stats: stats && {
      poolBalance: stats[0], totalTaxReceived: stats[1], totalPrizesPaid: stats[2],
      totalVrfFunded: stats[3], totalPortalFee: stats[4], totalDraws: stats[5],
      tier1Wins: stats[6], tier2Wins: stats[7], tier3Wins: stats[8], noPrizeCount: stats[9],
    },
    prizeConfig: prize && {
      prob1Bps: prize[0], prob2Bps: prize[1], prob3Bps: prize[2], noPrizeBps: prize[3],
      prize1: prize[4], prize2: prize[5], prize3: prize[6],
    },
    lastWin: lastWin && { winner: lastWin[0], amount: lastWin[1], tier: lastWin[2], at: lastWin[3] },
    lotteryEnabled: r(3) ?? true,
    holdingThreshold: r(4) ?? 0n,
    cooldown: r(5) ?? 3600n,
    minHoldTime: r(6) ?? 900n,
    taxToken: r(7) || TOKEN_ADDRESS,
  };
}

// 当前用户状态批量查询
export function useUserState() {
  const { address } = useAccount();
  const tokenAddr = TOKEN_ADDRESS;

  const { data, refetch } = useReadContracts({
    allowFailure: true,
    query: { enabled: !!address && hasVault, refetchInterval: 6_000 },
    contracts:
      address && hasVault
        ? [
            { address: VAULT_ADDRESS, abi: VAULT_ABI, functionName: "getUserStats", args: [address] },
            { address: VAULT_ADDRESS, abi: VAULT_ABI, functionName: "getCheckInStatus", args: [address] },
            ...(tokenAddr ? [{ address: tokenAddr, abi: ERC20_ABI, functionName: "balanceOf", args: [address] }] : []),
          ]
        : [],
  });

  if (!address || !hasVault || !data) {
    return { connected: !!address, hasVault, user: null, checkIn: null, tokenBalance: 0n, refetch };
  }

  const u = data[0]?.result;
  const c = data[1]?.result;
  const bal = tokenAddr ? data[2]?.result ?? 0n : 0n;

  return {
    connected: true, hasVault, refetch,
    user: u && {
      eligible: u[0], tokenBalance: u[1], lastDrawAt: u[2], nextDrawAt: u[3],
      drawCount: u[4], prizeWon: u[5],
    },
    checkIn: c && {
      checkedIn: c[0], balanceAt: c[1], at: c[2], secsLeft: c[3], canDraw: c[4],
    },
    tokenBalance: bal,
  };
}
